

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaLaptopCode, FaBook, FaUsers, FaFileAlt, FaCalendarAlt, FaCode } from 'react-icons/fa';
import { motion } from 'framer-motion';

const CompanyPlacementPage = () => {
  // State for real-time prep resources
  const [prepResources, setPrepResources] = useState([]);

  // Company data with real-time links
  const companies = {
    serviceBased: [
      {
        id: 'tcs',
        name: 'TCS',
        description: 'India’s largest IT services company, known for digital transformation solutions.',
        interviewProcess: ['Aptitude Test', 'Technical Interview', 'HR Round'],
        syllabus: ['Data Structures', 'OOPs', 'DBMS', 'Operating Systems'],
        resources: {
          coding: 'https://www.hackerrank.com/challenges/tcs-codevita',
          mcqs: 'https://www.indiabix.com/placement-papers/tcs/',
          pdf: 'https://prepinsta.com/tcs-nqt/how-to-crack-tcs-nqt/',
        },
      },
      {
        id: 'infosys',
        name: 'Infosys',
        description: 'Global leader in next-gen digital services and consulting.',
        interviewProcess: ['Online Test', 'Technical + HR Interview'],
        syllabus: ['Puzzles', 'Networking Basics', 'DBMS'],
        resources: {
          coding: 'https://www.hackerearth.com/recruit/infosys/',
          mcqs: 'https://www.indiabix.com/placement-papers/infosys/',
          pdf: 'https://www.prepinsta.com/infosys/placement-papers/',
        },
      },
      {
        id: 'wipro',
        name: 'Wipro',
        description: 'Indian multinational IT consulting and business process outsourcing company.',
        interviewProcess: ['Written Exam', 'Technical Interview', 'HR Interview'],
        syllabus: ['C/C++', 'OS Concepts', 'DBMS', 'Computer Networks'],
        resources: {
          coding: 'https://www.hackerearth.com/recruit/wipro/',
          mcqs: 'https://www.indiabix.com/placement-papers/wipro/',
          pdf: 'https://www.prepinsta.com/wipro/placement-papers/',
        },
      },
      {
        id: 'accenture',
        name: 'Accenture',
        description: 'Multinational professional services company specializing in IT services.',
        interviewProcess: ['Cognitive & Technical Assessment', 'Interview'],
        syllabus: ['Aptitude', 'Coding', 'Communication'],
        resources: {
          coding: 'https://www.hackerrank.com/challenges/accenture-coding',
          mcqs: 'https://www.indiabix.com/placement-papers/accenture/',
          pdf: 'https://www.prepinsta.com/accenture/placement-papers/',
        },
      },
      {
        id: 'capgemini',
        name: 'Capgemini',
        description: 'French multinational IT services and consulting company.',
        interviewProcess: ['Written Test', 'Group Discussion', 'Interview'],
        syllabus: ['Quantitative Aptitude', 'Logical Reasoning', 'Pseudocodes'],
        resources: {
          coding: 'https://www.hackerearth.com/recruit/capgemini/',
          mcqs: 'https://www.indiabix.com/placement-papers/capgemini/',
          pdf: 'https://www.prepinsta.com/capgemini/placement-papers/',
        },
      },
      {
        id: 'cognizant',
        name: 'Cognizant',
        description: 'American multinational IT services and consulting company.',
        interviewProcess: ['Aptitude Test', 'Technical Interview', 'HR Interview'],
        syllabus: ['DSA', 'OOPs', 'DBMS', 'Basic Programming'],
        resources: {
          coding: 'https://www.hackerrank.com/challenges/cognizant-coding',
          mcqs: 'https://www.indiabix.com/placement-papers/cognizant/',
          pdf: 'https://www.prepinsta.com/cognizant/placement-papers/',
        },
      },
      {
        id: 'hcl',
        name: 'HCL Technologies',
        description: 'Global IT services company focused on digital, IoT, and cloud solutions.',
        interviewProcess: ['Aptitude Test', 'Technical Interview', 'HR Interview'],
        syllabus: ['Quantitative Aptitude', 'Logical Reasoning', 'C/C++', 'DBMS'],
        resources: {
          coding: 'https://www.hackerearth.com/recruit/hcl/',
          mcqs: 'https://www.indiabix.com/placement-papers/hcl/',
          pdf: 'https://www.prepinsta.com/hcl/placement-papers/',
        },
      },
      {
        id: 'techmahindra',
        name: 'Tech Mahindra',
        description: 'Indian IT services company specializing in telecom and digital solutions.',
        interviewProcess: ['Online Test', 'Technical Interview', 'HR Round'],
        syllabus: ['Aptitude', 'Coding', 'Networking', 'OS'],
        resources: {
          coding: 'https://www.hackerearth.com/recruit/tech-mahindra/',
          mcqs: 'https://www.indiabix.com/placement-papers/tech-mahindra/',
          pdf: 'https://www.prepinsta.com/tech-mahindra/placement-papers/',
        },
      },
    ],
    productBased: [
      {
        id: 'google',
        name: 'Google',
        description: 'Leading tech giant known for innovation in search, cloud, and AI.',
        interviewProcess: ['Online Coding Round', 'Technical Interviews (3-4)', 'Behavioral Interview'],
        syllabus: ['Algorithms', 'Data Structures', 'System Design', 'Problem Solving'],
        resources: {
          coding: 'https://leetcode.com/company/google/',
          mcqs: 'https://www.geeksforgeeks.org/google-interview-preparation/',
          pdf: 'https://www.careercup.com/page?pid=google-interview-questions',
        },
      },
      {
        id: 'microsoft',
        name: 'Microsoft',
        description: 'Global leader in software, cloud computing, and AI solutions.',
        interviewProcess: ['Online Test', 'Technical Interviews (4-5)', 'HR Round'],
        syllabus: ['DSA', 'System Design', 'Coding', 'OOPs'],
        resources: {
          coding: 'https://leetcode.com/company/microsoft/',
          mcqs: 'https://www.geeksforgeeks.org/microsoft-interview-preparation/',
          pdf: 'https://www.careercup.com/page?pid=microsoft-interview-questions',
        },
      },
      {
        id: 'amazon',
        name: 'Amazon',
        description: 'E-commerce and cloud computing pioneer with a focus on innovation.',
        interviewProcess: ['Online Assessment', 'Technical Interviews (4-5)', 'Bar Raiser Round'],
        syllabus: ['DSA', 'System Design', 'Leadership Principles', 'Coding'],
        resources: {
          coding: 'https://leetcode.com/company/amazon/',
          mcqs: 'https://www.geeksforgeeks.org/amazon-interview-preparation/',
          pdf: 'https://www.careercup.com/page?pid=amazon-interview-questions',
        },
      },
      {
        id: 'adobe',
        name: 'Adobe',
        description: 'Leader in creative software and digital experience solutions.',
        interviewProcess: ['Online Test', 'Technical Interviews (3-4)', 'HR Interview'],
        syllabus: ['DSA', 'Algorithms', 'OS', 'Problem Solving'],
        resources: {
          coding: 'https://leetcode.com/company/adobe/',
          mcqs: 'https://www.geeksforgeeks.org/adobe-interview-preparation/',
          pdf: 'https://www.careercup.com/page?pid=adobe-interview-questions',
        },
      },
      {
        id: 'facebook',
        name: 'Meta (Facebook)',
        description: 'Social media and tech innovator focused on connectivity and AI.',
        interviewProcess: ['Online Coding Round', 'Technical Interviews (3-4)', 'Behavioral Round'],
        syllabus: ['DSA', 'System Design', 'Algorithms', 'Coding'],
        resources: {
          coding: 'https://leetcode.com/company/facebook/',
          mcqs: 'https://www.geeksforgeeks.org/facebook-interview-preparation/',
          pdf: 'https://www.careercup.com/page?pid=facebook-interview-questions',
        },
      },
      {
        id: 'apple',
        name: 'Apple',
        description: 'Global leader in consumer electronics and software innovation.',
        interviewProcess: ['Online Test', 'Technical Interviews (4-5)', 'Team Fit Interview'],
        syllabus: ['DSA', 'System Design', 'OS', 'Problem Solving'],
        resources: {
          coding: 'https://leetcode.com/company/apple/',
          mcqs: 'https://www.geeksforgeeks.org/apple-interview-preparation/',
          pdf: 'https://www.careercup.com/page?pid=apple-interview-questions',
        },
      },
      {
        id: 'netflix',
        name: 'Netflix',
        description: 'Streaming and entertainment giant with cutting-edge tech infrastructure.',
        interviewProcess: ['Online Coding', 'Technical Interviews (3-4)', 'Culture Fit Interview'],
        syllabus: ['DSA', 'System Design', 'Cloud Computing', 'Coding'],
        resources: {
          coding: 'https://leetcode.com/company/netflix/',
          mcqs: 'https://www.geeksforgeeks.org/netflix-interview-preparation/',
          pdf: 'https://www.careercup.com/page?pid=netflix-interview-questions',
        },
      },
    ],
  };

  // Simulate fetching real-time preparation resources
  useEffect(() => {
    const fetchPrepResources = async () => {
      // Placeholder for real-time API (e.g., LeetCode, X posts, or GeeksforGeeks)
      const mockResources = [
        {
          id: 1,
          title: 'Latest DSA Problems on LeetCode',
          source: 'LeetCode',
          link: 'https://leetcode.com/problemset/all-code-essentials/',
        },
        {
          id: 2,
          title: 'Top System Design Questions for Amazon',
          source: 'GeeksforGeeks',
          link: 'https://www.geeksforgeeks.org/amazon-interview-preparation/',
        },
        {
          id: 3,
          title: 'TCS CodeVita Practice Challenges ',
          source: 'HackerRank',
          link: 'https://prepinsta.com/tcs-codevita/practice-questions-with-answers/',
        },
      ];
      setPrepResources(mockResources);
    };

    fetchPrepResources();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0F20] text-white font-sans">
      <div className="max-w-7xl mx-auto py-16 px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl sm:text-5xl font-extrabold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent mb-4">
            Master Your Tech Placement
          </h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Prepare with confidence using our curated resources, real-time tools, and community support for top tech companies.
          </p>
        </motion.div>

        {/* Guidance Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-semibold text-gray-100 mb-6 text-center">
            Comprehensive Placement Preparation
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto text-center mb-8">
            Equip yourself with the best tools and community support to ace technical interviews and land your dream job.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-[#121624] p-6 rounded-lg shadow-lg border border-gray-600 opacity-50"
              title="Coming Soon"
            >
              <FaBook className="w-8 h-8 text-orange-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-gray-400">Personalized Mentorship</h3>
              <p className="text-gray-500">
                Connect with industry experts for tailored guidance. (Coming Soon)
              </p>
              <span className="mt-4 inline-block text-orange-400">Coming Soon</span>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-[#121624] p-6 rounded-lg shadow-lg border border-gray-600"
            >
              <FaLaptopCode className="w-8 h-8 text-blue-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Coding & Aptitude Practice</h3>
              <p className="text-gray-300">
                Solve thousands of company-specific coding problems, aptitude tests, and puzzles.
              </p>
              {/* <Link
                to="/practice"
                className="mt-4 inline-block text-blue-400 hover:text-blue-300"
              >
                Start Practicing
              </Link> */}
              <a
                href="https://www.hackerrank.com/domains/algorithms"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block text-blue-400 hover:text-blue-300"
              >
                start Practicing
              </a>

            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-[#121624] p-6 rounded-lg shadow-lg border border-gray-600"
            >
              <FaUsers className="w-8 h-8 text-blue-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2">Community Forums</h3>
              <p className="text-gray-300">
                Join peers and alumni to share interview experiences and prep strategies.
              </p>
              <Link
                to="/community"
                className="mt-4 inline-block text-blue-400 hover:text-blue-300"
              >
                Join the Community
              </Link>
            </motion.div>
          </div>
        </motion.div>

        {/* Preparation Tools Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-semibold text-gray-100 mb-6 text-center">
            Essential Preparation Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-[#121624] p-6 rounded-lg shadow-lg border border-gray-600 opacity-50"
              title="Coming Soon"
            >
              <FaFileAlt className="w-8 h-8 text-orange-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-gray-400">Resume Builder</h3>
              <p className="text-gray-500">
                Craft an ATS-optimized resume with guided templates. (Coming Soon)
              </p>
              <span className="mt-4 inline-block text-orange-400">Coming Soon</span>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-[#121624] p-6 rounded-lg shadow-lg border border-gray-600 opacity-50"
              title="Coming Soon"
            >
              <FaCalendarAlt className="w-8 h-8 text-orange-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-gray-400">Mock Interview Scheduler</h3>
              <p className="text-gray-500">
                Book mock interviews with experts for real-world practice. (Coming Soon)
              </p>
              <span className="mt-4 inline-block text-orange-400">Coming Soon</span>
            </motion.div>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="bg-[#121624] p-6 rounded-lg shadow-lg border border-gray-600 opacity-50"
              title="Coming Soon"
            >
              <FaCode className="w-8 h-8 text-orange-400 mb-4" />
              <h3 className="text-lg font-semibold mb-2 text-gray-400">Study Planner</h3>
              <p className="text-gray-500">
                Create a personalized study plan with company-specific roadmaps. (Coming Soon)
              </p>
              <span className="mt-4 inline-block text-orange-400">Coming Soon</span>
            </motion.div>
          </div>
        </motion.div>

        {/* Real-Time Prep Resources Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.5 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-semibold text-gray-100 mb-6 text-center">
            Real-Time Preparation Resources
          </h2>
          <p className="text-gray-300 max-w-3xl mx-auto text-center mb-8">
            Access the latest coding challenges, interview questions, and prep tips from top platforms.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {prepResources.map((resource) => (
              <motion.div
                key={resource.id}
                whileHover={{ scale: 1.05 }}
                className="bg-[#121624] p-4 rounded-lg shadow-lg border border-gray-600"
              >
                <h3 className="text-lg font-semibold">{resource.title}</h3>
                <p className="text-gray-400 text-sm">Source: {resource.source}</p>
                <a
                  href={resource.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-block text-blue-400 hover:text-blue-300"
                >
                  Explore Now
                </a>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Service-Based Companies */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0, duration: 0.5 }}
          className="mb-12"
        >
          <h2 className="text-xl font-semibold text-gray-100 mb-4">Service-Based Companies</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {companies.serviceBased.map((company) => (
              <Link
                key={company.id}
                to={`/company/${company.id}`}
                className="flex items-center justify-center border border-gray-600 rounded-lg py-3 px-4 text-sm bg-[#121624] hover:bg-[#1f2937] transition-colors shadow-sm"
              >
                {company.name}
              </Link>
            ))}
          </div>
        </motion.div>

        {/* Product-Based Companies */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          className="mb-12"
        >
          <h2 className="text-xl font-semibold text-gray-100 mb-4">Product-Based Companies</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {companies.productBased.map((company) => (
              <Link
                key={company.id}
                to={`/company/${company.id}`}
                className="flex items-center justify-center border border-gray-600 rounded-lg py-3 px-4 text-sm bg-[#121624] hover:bg-[#1f2937] transition-colors shadow-sm"
              >
                {company.name}
              </Link>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Floating Button */}
      {/* <Link to="/get-started">
        <div className="fixed bottom-6 right-6">
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            className="bg-blue-600 text-white rounded-full p-3 shadow-lg hover:bg-blue-700 transition flex items-center"
            aria-label="Get Started"
          >
            <FaLaptopCode className="w-6 h-6" />
          </motion.button>
        </div>
      </Link> */}
    </div>
  );
};

export default CompanyPlacementPage;